import { Fragment, ReactNode } from "react";
import { IoClose } from "react-icons/io5";
import styled from "styled-components";

import Button from "../../actions/Button";
import TextInput from "../TextInput";

const Styled = styled.div`
    border: 1px solid #0002;
    border-radius: 4px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px;
    width: 100%;

    ol {
        display: grid;
        grid-template-columns: 1fr min-content;
        gap: 2px 4px;
        list-style: none;
        margin: 0;
        padding: 0;

        &:empty {
            display: none;
        }

        li {
            border-bottom: 1px solid #0001;
            display: grid;
            grid-column: 1 / -1;
            grid-template-columns: subgrid;
            padding-bottom: 2px;
        }
    }
`;

interface IAddProps<T> {
    label: string
    onAdd: (value: T) => void
}

interface IInputProps<T> {
    all: T[]
    i: number
    label: string
    onChange: (value: T) => void
    onMove: (direction: number) => void
    value: T
}

interface IListInputProps<T = unknown> {
    Add?: (props: IAddProps<T>) => ReactNode
    Input?: (props: IInputProps<T>) => ReactNode
    disabled?: boolean
    itemKey?: (value: T) => string
    label: string
    onChange: (value: T) => void
    value: T[]
}

export default function ListInput(props: IListInputProps) {
    const {
        Add = DefaultAdd,
        Input = DefaultInput,
        disabled,
        itemKey = (item, array) => array.indexOf(item),
        label,
        onChange,
        value,
        ...restProps
    } = props;

    function move(i, direction) {
        const newValue = value.toSpliced(i, 1);
        newValue.splice(i + direction, 0, value[i]);
        onChange(newValue);
    }

    function update(i, newItem) {
        if (i === undefined) {
            onChange([...value, newItem]);
        } else {
            if (newItem === undefined) {
                onChange(
                    value.toSpliced(i, 1)
                );
            } else {
                onChange(
                    value.with(i, newItem)
                );
            }
        }
    }

    return (
        <Styled { ...restProps }>
            <ol>
                {
                    value.map(
                        (item, i) => (
                            <li key={ itemKey(item, value) ?? i }>
                                <Input
                                    all={ value }
                                    disabled={ disabled }
                                    i={ i }
                                    label={ label }
                                    onChange={ (updated) => update(i, updated) }
                                    onMove={ (direction) => move(i, direction) }
                                    value={ item }
                                />
                                <Button
                                    $appearance="subtle"
                                    $size="icon"
                                    disabled={ disabled }
                                    onClick={ () => update(i, undefined) }
                                >
                                    <IconX />
                                </Button>
                            </li>
                        )
                    )
                }
            </ol>
            <Add
                disabled={ disabled }
                label={ label }
                onAdd={ (value) => update(undefined, value) }
            />
        </Styled>
    );
}

const DefaultInput = styled(TextInput).attrs(
    (props: IInputProps<string>) => (
        {
            placeholder: props.label,
        }
    )
)`
    border: none;
`;

const DefaultAdd = styled(Button).attrs(
    (props: IAddProps<string>) => (
        {
            children: <Fragment>Add { props.label }</Fragment>,
            onClick() {
                props.onAdd("");
            },
        }
    )
)`
    justify-self: start;
`;

const IconX = styled(IoClose)`
    font-size: 1.2em;
`;
